/**
 * lib/reminderDelivery.ts
 * ------------------------------------------------------------------
 * WHAT A FEE REMINDER ACTUALLY ACHIEVED.
 *
 * The school sent 905 reminders and the bursar's inbox filled with
 * bounces, while every screen in the portal reported success. Three
 * different things were being confused with each other, and this module
 * exists to keep them apart:
 *
 *   SENT is what we did. The message left the building.
 *   ACCEPTED is what the mail server said. It can accept a message and
 *     still bounce it minutes later, out of band, to the sender's inbox
 *     — which is exactly what happened here. Accepted is NOT delivered.
 *   READ is what the parent did, and nothing in this system can see it.
 *     No open tracking, no read receipts. We do not claim it.
 *
 * So there is no "received" number to report, and inventing one would be
 * worse than admitting the gap. What CAN be known with certainty, before
 * a single message is sent, is whether an address is capable of
 * receiving mail at all — and that is where LKC's failure actually was.
 * 577 pupils carried an address at parent.lkc.ac.bw, a domain with no
 * mail exchanger, whose local part was the pupil's own surname. Those
 * were never parent addresses; they were manufactured by an import.
 *
 * A domain-level fault is knowable in advance, and a reminder sent to a
 * domain that cannot receive mail is not a failed delivery — it is a
 * chase that never happened. Counting it as sent is how a family goes
 * unchased for a year while the ledger says otherwise.
 * ------------------------------------------------------------------ */

/** Domains that exist in the data but cannot receive mail. Kept as data
 *  rather than a hardcoded test so the office can add to it, and so the
 *  server-side MX check can extend it with whatever it actually finds. */
export const KNOWN_DEAD_DOMAINS = [
  'parent.lkc.ac.bw',   // the import's manufactured parent addresses — no MX
  'nomail.com',         // the admissions form's "none given" placeholder
  'example.com',
];

/** `updatedBy` and friends hold a staff account, not a contact. An
 *  address at the school's own admin domain is never a parent. */
export const NOT_A_PARENT_DOMAINS = ['lkcportal.com'];

export type AddressStatus =
  | 'ok'
  /** Nothing on file. */
  | 'missing'
  | 'malformed'
  /** The DOMAIN cannot receive mail — a whole class of addresses. */
  | 'undeliverable'
  /** THIS address was refused by a real mail server, and we have the
   *  bounce. Kept apart from `undeliverable` because the evidence and the
   *  remedy differ: a dead domain is an import to fix, while a single
   *  refused address is usually a typo somebody can correct — 'gamil.com'
   *  for 'gmail.com' — and the office will only believe that if the
   *  screen can say a server actually rejected it. */
  | 'bounced'
  | 'notAParent';

export interface AddressVerdict {
  status: AddressStatus;
  /** Plain enough to show a bursar without translation. */
  reason: string;
  domain: string;
}

const SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Can this address receive a reminder?
 *
 * Deliberately conservative: anything we are not sure about comes back
 * `ok`, because refusing to chase a family over a hunch is worse than
 * sending a message that might bounce.
 */
export function classifyAddress(
  email: string | null | undefined,
  deadDomains: string[] = KNOWN_DEAD_DOMAINS,
  /** Addresses a real bounce has condemned — see MAIL_COLLECTIONS in
   *  lib/mailbox. Lower-cased. */
  condemned?: Iterable<string>,
): AddressVerdict {
  const raw = (email ?? '').trim();
  if (!raw) return { status: 'missing', reason: 'No address on file.', domain: '' };
  if (!SHAPE.test(raw)) {
    return { status: 'malformed', reason: 'That is not a usable email address.', domain: '' };
  }

  const domain = raw.split('@').pop()!.toLowerCase();

  if (NOT_A_PARENT_DOMAINS.includes(domain)) {
    return {
      status: 'notAParent',
      reason: `${domain} is a school account, not a parent's address.`,
      domain,
    };
  }
  if (deadDomains.map((d) => d.toLowerCase()).includes(domain)) {
    return {
      status: 'undeliverable',
      reason: `${domain} cannot receive mail — nothing sent there arrives.`,
      domain,
    };
  }

  /* Checked AFTER the domain, because a dead domain explains a whole
     class of addresses at once and is the more useful thing to tell
     somebody looking at 577 of them. */
  if (condemned) {
    const lower = raw.toLowerCase();
    for (const c of condemned) {
      if (String(c).toLowerCase() === lower) {
        return {
          status: 'bounced',
          reason: 'A message to this address was refused by the receiving mail server.',
          domain,
        };
      }
    }
  }

  return { status: 'ok', reason: '', domain };
}

/** An address that is worth sending to. */
export function isReachable(
  email: string | null | undefined,
  dead?: string[],
  condemned?: Iterable<string>,
): boolean {
  return classifyAddress(email, dead, condemned).status === 'ok';
}

/* ==================== what the log actually recorded =============== */

export type DeliveryOutcome = 'sent' | 'rejected' | 'failed' | 'no-address';

export interface DeliveryEntry {
  id: string;
  /** Present on delivery records and absent on the sweep's idempotency
   *  sentinels, which share the collection. This is what tells them
   *  apart — see `isDeliveryRecord`. */
  atIso?: string;
  outcome?: DeliveryOutcome;
  /** 'digest' only ever appears on the sweep's daily summary to the
   *  bursar, which shares this collection and is not a parent reminder. */
  kind?: 'before' | 'after' | 'digest';
  studentId?: string;
  studentName?: string;
  invoiceNo?: string;
  instalment?: string;
  to?: string;
  reason?: string;
  /** A uid, or the literal 'scheduled' for the 07:00 sweep. */
  by?: string;
}

const OUTCOMES: DeliveryOutcome[] = ['sent', 'rejected', 'failed', 'no-address'];

/**
 * The reminder log holds two unrelated kinds of document: the delivery
 * records written for every attempt, and the sweep's per-instalment
 * sentinels that stop it chasing the same instalment twice. Only the
 * first kind is a delivery, and counting the sentinels would roughly
 * double every figure on the screen.
 */
export function isDeliveryRecord(e: DeliveryEntry): boolean {
  return Boolean(e.atIso) && OUTCOMES.includes(e.outcome as DeliveryOutcome);
}

export interface DeliverySummary {
  attempted: number;
  /** The mail server took it. Not the same as delivered, and not read. */
  accepted: number;
  /** Refused at send time — the one failure the sending code can see. */
  rejected: number;
  /** The send threw: no connection, bad credentials, a server error. */
  failed: number;
  /** Never attempted, because there was no address to attempt. */
  noAddress: number;
  /** Most recent attempt of any kind, ISO, or '' when there are none. */
  lastAttemptAt: string;
}

export function summarise(entries: DeliveryEntry[]): DeliverySummary {
  const rows = entries.filter(isDeliveryRecord);
  const s: DeliverySummary = {
    attempted: 0, accepted: 0, rejected: 0, failed: 0, noAddress: 0, lastAttemptAt: '',
  };
  for (const e of rows) {
    if (e.outcome === 'no-address') s.noAddress += 1;
    else {
      s.attempted += 1;
      if (e.outcome === 'sent') s.accepted += 1;
      else if (e.outcome === 'rejected') s.rejected += 1;
      else if (e.outcome === 'failed') s.failed += 1;
    }
    if ((e.atIso ?? '') > s.lastAttemptAt) s.lastAttemptAt = e.atIso ?? '';
  }
  return s;
}

/** Attempts per day, newest first — enough to see whether the 07:00
 *  sweep is running at all, which is the question a silent scheduled job
 *  never answers on its own. */
export function byDay(entries: DeliveryEntry[]): { day: string; attempted: number; accepted: number }[] {
  const days = new Map<string, { day: string; attempted: number; accepted: number }>();
  for (const e of entries.filter(isDeliveryRecord)) {
    const day = (e.atIso ?? '').slice(0, 10);
    if (!day) continue;
    const row = days.get(day) ?? { day, attempted: 0, accepted: 0 };
    if (e.outcome !== 'no-address') {
      row.attempted += 1;
      if (e.outcome === 'sent') row.accepted += 1;
    }
    days.set(day, row);
  }
  return [...days.values()].sort((a, b) => b.day.localeCompare(a.day));
}

/** Everything that did not get through, worst first, so the office has a
 *  work list rather than a statistic. */
export function problems(entries: DeliveryEntry[]): DeliveryEntry[] {
  const rank: Record<string, number> = { rejected: 0, failed: 1, 'no-address': 2 };
  return entries
    .filter(isDeliveryRecord)
    .filter((e) => e.outcome !== 'sent')
    .sort((a, b) => (rank[a.outcome!] - rank[b.outcome!])
      || (b.atIso ?? '').localeCompare(a.atIso ?? ''));
}

/* ==================== who we could reach if we tried =============== */

export interface ReachRow {
  studentId: string;
  studentName: string;
  /** The address the reminder would use today. */
  current: string | null;
  /** A usable address held elsewhere on the record — the admissions
   *  guardian — when the current one cannot receive mail. */
  alternative?: { email: string; who: string; field: string } | null;
  outstanding: number;
}

export type ReachBand = 'reachable' | 'recoverable' | 'unreachable';

/**
 * Three states, and the middle one is the finding.
 *
 * `recoverable` means the school already holds an address that works —
 * the parent wrote it on the application form — and the reminder is
 * being sent somewhere else. Nobody has to collect anything; the data is
 * already there, in the wrong field.
 */
export function bandOf(row: ReachRow, dead?: string[], condemned?: Iterable<string>): ReachBand {
  if (isReachable(row.current, dead, condemned)) return 'reachable';
  if (row.alternative && isReachable(row.alternative.email, dead, condemned)) return 'recoverable';
  return 'unreachable';
}

export interface ReachReport {
  reachable: { pupils: number; outstanding: number };
  recoverable: { pupils: number; outstanding: number };
  unreachable: { pupils: number; outstanding: number };
  /** Dead domains actually seen, with how many pupils sit on each. */
  deadDomains: { domain: string; pupils: number }[];
  /** Pupils whose own address a real bounce has condemned — a typo to
   *  correct, not a domain to migrate. */
  bounced: { pupils: number; outstanding: number };
}

export function reachReport(
  rows: ReachRow[],
  dead?: string[],
  condemned?: Iterable<string>,
): ReachReport {
  const r: ReachReport = {
    reachable: { pupils: 0, outstanding: 0 },
    recoverable: { pupils: 0, outstanding: 0 },
    unreachable: { pupils: 0, outstanding: 0 },
    deadDomains: [],
    bounced: { pupils: 0, outstanding: 0 },
  };
  const domains = new Map<string, number>();

  for (const row of rows) {
    const band = bandOf(row, dead, condemned);
    r[band].pupils += 1;
    r[band].outstanding = round2(r[band].outstanding + Math.max(0, row.outstanding));

    const v = classifyAddress(row.current, dead, condemned);
    if (v.status === 'undeliverable' || v.status === 'notAParent') {
      domains.set(v.domain, (domains.get(v.domain) ?? 0) + 1);
    }
    if (v.status === 'bounced') {
      r.bounced.pupils += 1;
      r.bounced.outstanding = round2(r.bounced.outstanding + Math.max(0, row.outstanding));
    }
  }

  r.deadDomains = [...domains.entries()]
    .map(([domain, pupils]) => ({ domain, pupils }))
    .sort((a, b) => b.pupils - a.pupils);
  return r;
}


/* ============ sends that predate the delivery logging ============= */

/**
 * THE SENTINELS ARE EVIDENCE TOO, and treating them as noise made the
 * screen say "nothing has been sent yet" to a school that had sent 905
 * reminders the day before.
 *
 * The sweep has always written one sentinel per instalment to stop a
 * family being chased twice. It records that a message went out and to
 * what address — but it predates any notion of an outcome, so it cannot
 * say what became of it. That is a real and reportable state: SENT,
 * OUTCOME UNKNOWN. It is not a success, and it is not nothing.
 *
 * And the address it carries is the useful part. A sentinel pointing at
 * a domain with no mail exchanger is a reminder we now know never
 * arrived, however confidently it was logged at the time.
 */
export interface SentRecord {
  id: string;
  invoiceId?: string;
  instalment?: number | string;
  kind?: 'before' | 'after' | 'digest';
  to?: string;
  /** Normalised from the Firestore Timestamp the sentinel actually holds. */
  sentAtIso: string;
}

/** The sweep's daily digest to the bursar shares the collection and is
 *  not a reminder to a parent. */
export function isSentinel(e: DeliveryEntry & { to?: string; kind?: string }): boolean {
  return !isDeliveryRecord(e) && Boolean(e.to) && e.kind !== 'digest';
}

export interface LegacySends {
  total: number;
  /** Sent to an address that cannot receive mail — known to have failed,
   *  whatever the log said at the time. */
  toDeadAddress: number;
  /** Distinct dead domains among them, worst first. */
  deadDomains: { domain: string; sends: number }[];
  earliest: string;
  latest: string;
}

export function summariseSentinels(rows: SentRecord[], dead?: string[]): LegacySends {
  const domains = new Map<string, number>();
  let toDeadAddress = 0;
  let earliest = '';
  let latest = '';

  for (const r of rows) {
    const v = classifyAddress(r.to, dead);
    if (v.status === 'undeliverable' || v.status === 'notAParent') {
      toDeadAddress += 1;
      domains.set(v.domain, (domains.get(v.domain) ?? 0) + 1);
    }
    if (r.sentAtIso) {
      if (!earliest || r.sentAtIso < earliest) earliest = r.sentAtIso;
      if (r.sentAtIso > latest) latest = r.sentAtIso;
    }
  }

  return {
    total: rows.length,
    toDeadAddress,
    deadDomains: [...domains.entries()]
      .map(([domain, sends]) => ({ domain, sends }))
      .sort((a, b) => b.sends - a.sends),
    earliest,
    latest,
  };
}

function round2(n: number): number { return Math.round(n * 100) / 100; }
